import { usePostHog } from "posthog-js/react";
import type React from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { EntityPreview } from "@/components/EntityPreview/EntityPreview";
import { FormDocumentPicker } from "@/components/Form/FormDocumentPicker";
import { FormImagePicker } from "@/components/Form/FormImagePicker";
import { FormInput } from "@/components/Form/FormInput";
import { FormMentionComposer } from "@/components/Form/FormMentionComposer";
import { FormTextArea } from "@/components/Form/FormTextArea";
import { FormVideoPicker } from "@/components/Form/FormVideoPicker";
import { PollPreview } from "@/components/Poll/PollPreview";
import { ContentBlock } from "@/design-system/ContentBlock";
import { FormField } from "@/design-system/FormField";
import { Inline } from "@/design-system/Inline";
import { Stack } from "@/design-system/Stack";
import { unfurlDeepLinkFromContent } from "@/helpers/deep-link";
import {
  createDocumentSizeRule,
  createDocumentTypeRule,
  createImageSizeRule,
  createImageTypeRule,
  createRequiredStringRule,
  createVideoSizeRule,
  createVideoTypeRule,
} from "@/helpers/rules";
import { MAX_AMOUNT_IMAGES_PER_POST } from "@/modules/community-feed/constants";
import { useTranslation } from "@/translations";

import type { AdminCreatePostSharedFormValues } from "../AdminCreatePostPage";
import { AdminCreatePostFormAddPollButton } from "./AdminCreatePostFormAddPollButton";

interface AdminCreatePostFormEditorProps {
  contentAction?: React.ReactNode;
  mentionAudience?: {
    type: "group" | "project" | "projectConnection";
    id?: string;
  };
}

/** Renders post content and attachments, enabling audience-scoped mentions when an audience is available. */
export function AdminCreatePostFormEditor({
  contentAction,
  mentionAudience,
}: AdminCreatePostFormEditorProps): React.ReactNode {
  const { t } = useTranslation();
  const postHog = usePostHog();

  const form = useFormContext<AdminCreatePostSharedFormValues>();
  const currPostType = useWatch({ name: "postType", control: form.control });
  const currImages = useWatch({ name: "images", control: form.control });
  const currVideos = useWatch({ name: "videos", control: form.control });
  const currDocuments = useWatch({ name: "documents", control: form.control });
  const currContent = useWatch({ name: "content", control: form.control });
  const currPoll = useWatch({ name: "poll", control: form.control });

  const isAnyAttachmentSelected = currImages.length > 0 || currVideos.length > 0 || currDocuments.length > 0;
  const error =
    form.formState.errors["images"]?.message ||
    form.formState.errors["videos"]?.message ||
    form.formState.errors["documents"]?.message;
  const { entity: attachedEntity } = unfurlDeepLinkFromContent(currContent);

  return (
    <Stack gap="sm">
      <FormField label={t("page.admin-create-post.form.title")} required hideLabel>
        <FormInput<AdminCreatePostSharedFormValues, "title">
          name="title"
          data-testid="create-post-title-input"
          placeholder={t("page.admin-create-post.form.title.placeholder")}
          rules={{
            validate: {
              required: createRequiredStringRule(t, "page.admin-create-post.form.title"),
            },
          }}
        />
      </FormField>

      <FormField label={t("page.admin-create-post.form.message")} hideLabel>
        {mentionAudience == null ? (
          <FormTextArea<AdminCreatePostSharedFormValues, "content">
            name="content"
            data-testid="create-post-content-input"
            placeholder={t("page.admin-create-post.form.message.placeholder")}
            rules={{ validate: { required: createRequiredStringRule(t, "page.admin-create-post.form.message") } }}
            size={contentAction ? "post" : "lg"}
            scrollbar
          />
        ) : (
          <FormMentionComposer<AdminCreatePostSharedFormValues>
            name="content"
            data-testid="create-post-content-input"
            contentTemplateName="contentTemplate"
            mentionsName="mentions"
            placeholder={t("page.admin-create-post.form.message.placeholder")}
            rules={{ validate: { required: createRequiredStringRule(t, "page.admin-create-post.form.message") } }}
            groupId={mentionAudience.type === "group" ? mentionAudience.id : undefined}
            projectConnectionId={mentionAudience.type === "projectConnection" ? mentionAudience.id : undefined}
            type={currPostType}
            size="large"
          />
        )}
      </FormField>

      {contentAction}

      <FormField label={t("page.create-post.form.file")} hideLabel>
        <Stack gap="xs">
          <Inline gap="sm" align="start">
            {(!isAnyAttachmentSelected || currImages.length > 0) && (
              <FormImagePicker<AdminCreatePostSharedFormValues, "images">
                name="images"
                maxFiles={MAX_AMOUNT_IMAGES_PER_POST}
                disabled={currImages.length >= MAX_AMOUNT_IMAGES_PER_POST}
                rules={{
                  validate: {
                    size: createImageSizeRule({
                      t,
                      onError: () => postHog.capture("form_error_image_size_exceeded"),
                    }),
                    type: createImageTypeRule({ t }),
                  },
                }}
              />
            )}
            {(!isAnyAttachmentSelected || currVideos.length > 0) && (
              <FormVideoPicker<AdminCreatePostSharedFormValues, "videos">
                name="videos"
                maxFiles={1}
                disabled={currVideos.length > 0}
                rules={{
                  validate: {
                    size: createVideoSizeRule({
                      t,
                      onError: () => postHog.capture("form_error_video_size_exceeded"),
                    }),
                    type: createVideoTypeRule({ t }),
                  },
                }}
              />
            )}
            {(!isAnyAttachmentSelected || currDocuments.length > 0) && (
              <FormDocumentPicker<AdminCreatePostSharedFormValues, "documents">
                name="documents"
                maxFiles={1}
                disabled={currDocuments.length > 0}
                rules={{
                  validate: {
                    size: createDocumentSizeRule({
                      t,
                      onError: () => postHog.capture("form_error_document_size_exceed"),
                    }),
                    type: createDocumentTypeRule({ t }),
                  },
                }}
              />
            )}
            {!currPoll && (
              <AdminCreatePostFormAddPollButton
                onAddPoll={(poll) => {
                  form.setValue("poll", poll, { shouldDirty: true });
                }}
              />
            )}
          </Inline>
          {Boolean(error) && (
            <span
              data-testid="form-input-error"
              role="alert"
              className="text-overline whitespace-nowrap text-text-danger-secondary"
            >
              {error}
            </span>
          )}
        </Stack>
      </FormField>
      {currPoll && (
        <PollPreview
          poll={currPoll}
          onEdit={(poll) => form.setValue("poll", poll, { shouldDirty: true })}
          onDelete={() => form.setValue("poll", undefined)}
        />
      )}
      {attachedEntity && (
        <ContentBlock as="aside" width="flow" align="left" data-testid="attached-entity-preview">
          <EntityPreview
            entityId={attachedEntity.payload.entityId}
            entityType={attachedEntity.payload.entityType}
            otherIds={{
              folderId: attachedEntity.payload.folderId,
            }}
            readOnly
            shouldLoad
          />
        </ContentBlock>
      )}
    </Stack>
  );
}
